import React, { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import useApi from '../hooks/useApi';
import haxios from '../lib/haxios';
import { GlobalContext } from '../context/GlobalContext';
import DocumentsTab from '../components/vehicles/DocumentsTab';
import FuelLogsTab from '../components/vehicles/FuelLogsTab';
import IncidentsTab from '../components/vehicles/IncidentsTab';
import MaintenanceTab from '../components/vehicles/MaintenanceTab';

export default function VehiclePage() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { apir } = useContext(GlobalContext);

    const { data: vehicle, refetch, isLoading } = useApi({ url: `/visiondashboard/salesassist/vehicles/${id}`, initial: {} });
    const { data: driversResponse } = useApi({ url: `/visiondashboard/salesassist/drivers`, initial: { data: [] } });

    const drivers = driversResponse?.data || [];
    const [formData, setFormData] = useState({});
    const [selectedTab, setSelectedTab] = useState('details');
    const [isSaving, setIsSaving] = useState(false);

    useEffect(() => {
        if (vehicle && vehicle.data && Object.keys(vehicle.data).length > 0) {
            setFormData(vehicle.data);
        }
    }, [vehicle]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData({ ...formData, [name]: value });
    };

    const handleSave = async () => {
        setIsSaving(true);
        try {
            await haxios.put(`/visiondashboard/salesassist/vehicles/${id}`, formData);
            refetch();
        } catch (error) {
            console.error('Error saving vehicle:', error);
            alert('Failed to save vehicle details.');
        }
        setIsSaving(false);
    };

    if (isLoading) return <p className="text-center text-gray-600">Loading vehicle details...</p>;

    return (
        <div className="p-6 bg-gray-100 min-h-screen">
            <div className="max-w-7xl mx-auto bg-white p-6 rounded-lg shadow-xl">
                <h1 className="text-2xl font-bold text-gray-800 mb-4">Vehicle Details</h1>

                <div className="mb-6 flex space-x-4 border-b">
                    {["details", "documents", "fuel", "incidents", "maintenance"].map(tab => (
                        <button
                            key={tab}
                            className={`py-2 px-4 ${selectedTab === tab ? 'border-b-2 border-indigo-600 font-semibold' : 'text-gray-600'}`}
                            onClick={() => setSelectedTab(tab)}
                        >
                            {tab.charAt(0).toUpperCase() + tab.slice(1)}
                        </button>
                    ))}
                </div>

                {selectedTab === 'details' && (
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700">Branch</label>
                            <input
                                type="text"
                                name="branch"
                                value={formData.branch || ''}
                                onChange={handleInputChange}
                                className="mt-1 block w-full border rounded-md p-2"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700">Registration</label>
                            <input
                                type="text"
                                name="registration"
                                value={formData.registration || ''}
                                onChange={handleInputChange}
                                className="mt-1 block w-full border rounded-md p-2"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700">Make</label>
                            <input
                                type="text"
                                name="make"
                                value={formData.make || ''}
                                onChange={handleInputChange}
                                className="mt-1 block w-full border rounded-md p-2"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700">Model</label>
                            <input
                                type="text"
                                name="model"
                                value={formData.model || ''}
                                onChange={handleInputChange}
                                className="mt-1 block w-full border rounded-md p-2"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700">Year</label>
                            <input
                                type="number"
                                name="year"
                                value={formData.year || ''}
                                onChange={handleInputChange}
                                className="mt-1 block w-full border rounded-md p-2"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700">Next MOT</label>
                            <input
                                type="date"
                                name="nextMot"
                                value={formData.nextMot ? formData.nextMot.split('T')[0] : ''}
                                onChange={handleInputChange}
                                className="mt-1 block w-full border rounded-md p-2"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700">Tax Status</label>
                            <input
                                type="text"
                                name="taxStatus"
                                value={formData.taxStatus || ''}
                                onChange={handleInputChange}
                                className="mt-1 block w-full border rounded-md p-2"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700">Insured With</label>
                            <input
                                type="text"
                                name="insuredWith"
                                value={formData.insuredWith || ''}
                                onChange={handleInputChange}
                                className="mt-1 block w-full border rounded-md p-2"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700">Insurance Expiry</label>
                            <input
                                type="date"
                                name="insuredExpire"
                                value={formData.insuredExpire ? formData.insuredExpire.split('T')[0] : ''}
                                onChange={handleInputChange}
                                className="mt-1 block w-full border rounded-md p-2"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700">Mileage</label>
                            <input
                                type="number"
                                name="mileage"
                                value={formData.mileage || ''}
                                onChange={handleInputChange}
                                className="mt-1 block w-full border rounded-md p-2"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700">Vehicle Condition</label>
                            <input
                                type="text"
                                name="vehicleCondition"
                                value={formData.vehicleCondition || ''}
                                onChange={handleInputChange}
                                className="mt-1 block w-full border rounded-md p-2"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700">Assign Driver</label>
                            <select
                                name="current_driver_id"
                                value={formData.current_driver_id || ''}
                                onChange={handleInputChange}
                                className="mt-1 block w-full border rounded-md p-2"
                            >
                                <option value="">Unassigned</option>
                                {drivers.map(driver => (
                                    <option key={driver.staff_id} value={driver.staff_id}>
                                        {driver.Name} ({driver.license_number})
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>
                )}

                {selectedTab === 'documents' && <DocumentsTab vehicleId={id} />}
                {selectedTab === 'fuel' && <FuelLogsTab vehicleId={id} />}
                {selectedTab === 'incidents' && <IncidentsTab vehicleId={id} />}
                {selectedTab === 'maintenance' && <MaintenanceTab vehicleId={id} />}

                <div className="mt-6 flex space-x-3">
                    <button
                        onClick={() => navigate('/fleet/vehicles')}
                        className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md"
                    >
                        Back to Fleet
                    </button>
                    <button
                        onClick={handleSave}
                        className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-md"
                        disabled={isSaving}
                    >
                        {isSaving ? 'Saving...' : 'Save Changes'}
                    </button>
                </div>
            </div>
        </div>
    );
}
